<template>
  <TransitionRoot
    :show="open"
    as="div"
    class="relative z-40 top-0 bottom-0 overflow-auto flex flex-col h-full bg-neutral-white"
  >
    <TransitionChild
      as="div"
      enter="left-[352px] duration-300 ease-out"
      enter-from="opacity-0 translate-x-[-352px]"
      enter-to="opacity-100"
      leave="left-0 duration-200 ease-in"
      leave-from="opacity-100"
      leave-to="translate-x-[-352px] opacity-0"
    >
      <div class="w-full md:w-[450px] bg-neutral-white p-6 pt-10 z-50">
        <h3 class="text-xl font-geist mb-4">Select Brand</h3>
        <PulseInput
          ref="searchInput"
          v-model="query"
          :placeholder="groupingActive ? 'Search Brands' : 'Search...'"
          data-testid="store-select-search-input"
        >
          <template #suffix>
            <button
              v-if="query.length"
              type="button"
              class="p-2 -mr-2 text-primary-700"
              @click.stop.prevent="query = ''"
            >
              <PulseIconClose class="h-3 w-3 fill-path-current" />
            </button>
          </template>
        </PulseInput>
        <TabbedNav :items="navItems" :active="currentTab" compact @nav:click="handleChange">
          <template #item-active>
            {{ stores.active.length }}
          </template>
          <template #item-inactive>
            {{ stores.inactive.length }}
          </template>
          <template #item-onboarding>
            {{ stores.onboarding.length }}
          </template>
          <template #item-dev>
            {{ stores.dev.length }}
          </template>
          <template #default>
            <!-- Negative margin offsets TabbedNav's pt-8 content padding, which reads as dead space
                 in this narrow panel (scoped here — every other tabbed page relies on that rhythm). -->
            <div class="-mt-4">
              <div class="flex items-center gap-2">
                <button
                  :disabled="currentBrands.length === 0"
                  :class="{
                    'bg-primary-100': allSelectedCategory?.toLowerCase() === currentTab,
                    'opacity-50 cursor-not-allowed': currentBrands.length === 0,
                  }"
                  class="px-3 py-2 text-xsm capitalize font-medium text-typography-disabled cursor-pointer hover:bg-neutral-100 active:bg-neutral-200 w-full text-left flex items-center justify-between"
                  @click="setAllStores"
                >
                  All {{ currentTab }} {{ groupingActive ? 'Brands' : 'stores' }}
                  <PulseIconCheck v-if="allSelectedCategory?.toLowerCase() === currentTab" class="h-4 w-4" />
                </button>
                <PulseIconButton
                  variant="secondary"
                  icon="LineAlphabet"
                  :disabled="groupingActive"
                  :class="{ 'opacity-50': groupingActive }"
                  @click="separator = !separator"
                />
              </div>
              <div
                v-if="groupingActive"
                class="mt-1 flex items-center justify-end gap-1 text-2xs text-typography-disabled"
                data-testid="store-select-grouping-lock"
              >
                <PulseLineLock class="h-3 w-3 fill-current" />
                Grouped by Child Account
              </div>

              <NavDivider />

              <template v-if="groupingActive">
                <div v-if="!query && accountGroups.length > 1" class="flex justify-end px-3 pt-2">
                  <button
                    type="button"
                    class="text-2xs font-medium text-primary-700"
                    data-testid="store-select-toggle-all"
                    @click="toggleAllGroups"
                  >
                    {{ allGroupsExpanded ? 'Collapse all' : 'Expand all' }}
                  </button>
                </div>
                <div
                  v-if="searchSummary"
                  class="px-3 pt-2 text-2xs text-typography-secondary"
                  data-testid="store-select-search-summary"
                >
                  {{ searchSummary }}
                </div>

                <div
                  v-if="exactChildMatch"
                  class="px-3 py-8 text-center"
                  data-testid="store-select-child-account-empty-state"
                >
                  <p class="text-sm font-semibold text-typography-primary">No Brands match "{{ query }}"</p>
                  <p class="mt-2 text-xsm text-typography-secondary">
                    This search covers Brand names only. <span class="font-semibold">{{ exactChildMatch.name }}</span>
                    is a Child Account — open it from the list to see its
                    {{ pluralize(exactChildMatchBrandCount, 'Brand') }}.
                  </p>
                  <button
                    type="button"
                    class="mt-4 inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-neutral-white px-3 py-2 text-xsm font-semibold text-typography-primary hover:bg-neutral-100"
                    data-testid="store-select-open-child-account"
                    @click="openChildAccount(exactChildMatch)"
                  >
                    <span
                      class="flex h-4 w-4 items-center justify-center rounded bg-neutral-200 text-2xs font-bold text-neutral-700"
                    >
                      {{ childInitials(exactChildMatch.name) }}
                    </span>
                    Open {{ exactChildMatch.name }}
                  </button>
                </div>
                <div
                  v-else-if="query && !filteredBrands.length"
                  class="px-3 py-8 text-center text-sm font-semibold text-typography-primary"
                  data-testid="store-select-empty-state"
                >
                  No Brands match "{{ query }}"
                </div>

                <div v-for="group in accountGroups" :key="group.accountId">
                  <AccountRollupHeader
                    :label="group.label"
                    :count="group.totalCount"
                    :matched-count="query ? group.matchedCount : undefined"
                    :collapsed="isGroupCollapsed(group.accountId)"
                    class="px-3 py-2 hover:bg-neutral-100"
                    :data-testid="`store-select-group-${group.accountId}`"
                    @toggle="toggleGroup(group.accountId)"
                  />
                  <template v-if="!isGroupCollapsed(group.accountId)">
                    <div
                      v-for="brand in group.brands"
                      :key="brand.id"
                      class="hover:bg-neutral-100 active:bg-neutral-200 pl-8 pr-3 py-3 text-xsm font-medium cursor-pointer flex items-center justify-between text-typography-disabled dark:text-neutral-500"
                      :data-testid="`store-select-${brand.slug}`"
                      @click="closeStores(brand)"
                    >
                      <span>
                        <template v-for="(segment, index) in highlightSegments(brand.name)" :key="index">
                          <span :class="{ 'bg-warning-100 rounded px-0.5 font-semibold': segment.matched }">{{
                            segment.text
                          }}</span>
                        </template>
                      </span>
                      <PulseIconCheck v-if="brand.id === store.currentStore?.id" class="h-4 w-4" />
                    </div>
                  </template>
                </div>

                <div
                  v-if="hiddenGroupCount"
                  class="mt-3 border-t border-neutral-200 px-3 pt-3 text-2xs text-typography-disabled"
                  data-testid="store-select-hidden-groups-footer"
                >
                  {{ hiddenGroupCount }} Child Accounts hidden — no matching Brands
                </div>
              </template>

              <template v-else>
                <div v-for="(group, letter) in groupedBrands" :key="letter">
                  <div
                    v-if="separator"
                    class="flex items-center h-7 text-2xs font-semibold text-typography-disabled px-3 border-b border-neutral-100 mt-1 mb-1"
                  >
                    {{ letter }}
                  </div>
                  <div
                    v-for="brand in group"
                    :key="brand.id"
                    class="hover:bg-neutral-100 active:bg-neutral-200 px-3 py-3 text-xsm font-medium cursor-pointer flex items-center justify-between text-typography-disabled dark:text-neutral-500"
                    :data-testid="`store-select-${brand.slug}`"
                    @click="closeStores(brand)"
                  >
                    {{ brand.name }}
                    <PulseIconCheck v-if="brand.id === store.currentStore?.id" class="h-4 w-4" />
                  </div>
                </div>
              </template>
            </div>
          </template>
        </TabbedNav>
      </div>
    </TransitionChild>
  </TransitionRoot>
  <TransitionRoot :show="open" as="div" class="fixed inset-0">
    <TransitionChild
      as="div"
      class="z-50"
      enter="duration-300 ease-out"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave="duration-200 ease-in"
      leave-from="opacity-100"
      leave-to="opacity-0"
    >
      <div v-if="open" class="bg-black/25 fixed left-0 right-0 top-0 bottom-0 z-40" @click="$emit('close')" />
    </TransitionChild>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import { TransitionChild, TransitionRoot } from '@headlessui/vue';
import { storeToRefs } from 'pinia';

import { FEATURE_FLAGS } from '@constants';

import { useAccountHierarchy } from '~/composables/useAccountHierarchy';
import { useLaunchDarkly } from '~/composables/useLaunchDarkly';
import { fuzzyFactory } from '~/composables/useSearch';
import { useMerchantStore } from '~/store/merchant';
import type { Brand } from '~/types';
import AccountRollupHeader from './AccountRollupHeader.vue';

interface StoreWithSettings {
  id: string;
  accountId: string;
  generalSettings: any;
  storeUrl: string;
  storeDomain: string;
  slug: string;
  platformId: string;
  name: string;
  storeType: 'ACTIVE' | 'ONBOARDING' | 'INACTIVE' | 'DEV';
  capabilities: any;
  settings: {
    storeLogoUrl?: string;
  };
}

const navItems = [
  {
    label: 'Active',
    value: 'active',
  },
  {
    label: 'Onboarding',
    value: 'onboarding',
  },
  {
    label: 'Dev',
    value: 'dev',
  },
  {
    label: 'Inactive',
    value: 'inactive',
  },
];
const searchableFields = ['name'];

/** Above either threshold the Child Account groups start collapsed (ENG-5850 design 1t). */
const GROUP_COLLAPSE_THRESHOLD = 6;
const BRAND_COLLAPSE_THRESHOLD = 25;

type ChildAccountSummary = { id: string; name: string };

const emit = defineEmits(['close']);

const props = defineProps({
  open: {
    default: true,
    type: Boolean,
  },
});

const searchInput = ref<{ $el: HTMLElement } | null>(null);
const query = ref('');
const currentTab = ref('active');
const store = useMerchantStore();
const { stores, allSelectedCategory } = storeToRefs(store);
const separator = ref(false);

const currentBrands = computed(() => {
  return stores.value ? stores.value[currentTab.value as keyof typeof stores.value] : ([] as any[]);
});

const filteredBrands = computed(() => {
  return query.value
    ? fuzzyFactory(currentBrands.value, searchableFields)
        .search(query.value)
        .map(({ item }) => item)
    : currentBrands.value;
});

const groupedBrands = computed(() => {
  const groups: Record<string, any[]> = {};

  filteredBrands.value.forEach(brand => {
    const firstLetter = brand.name.charAt(0).toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(brand);
  });

  // Sort the groups alphabetically
  return Object.keys(groups)
    .sort()
    .reduce(
      (acc, key) => {
        acc[key] = groups[key];

        return acc;
      },
      {} as Record<string, any[]>,
    );
});

// Child Account grouping for Parent Account users (ENG-5850); everyone else keeps the A–Z drawer.
const { isParent } = useAccountHierarchy();
const { getFlag } = useLaunchDarkly();
const childAccounts = ref<ChildAccountSummary[]>([]);
const expandedOverrides = ref<Record<string, boolean>>({});

const hierarchyGroupingEnabled = computed(
  () => isParent.value && getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false) === true,
);
const groupingActive = computed(() => hierarchyGroupingEnabled.value && childAccounts.value.length > 0);

function loadChildAccounts() {
  if (childAccounts.value.length) return;

  useGateway()
    .identity.accounts.children()
    .subscribe({
      error: () => (childAccounts.value = []),
      next: response => (childAccounts.value = response?.data ?? []),
    });
}

watch(hierarchyGroupingEnabled, enabled => enabled && loadChildAccounts(), { immediate: true });

const childNameById = computed(() => new Map(childAccounts.value.map(child => [child.id, child.name])));

const totalsByAccount = computed(() => {
  const totals = new Map<string, number>();

  currentBrands.value.forEach(brand => totals.set(brand.accountId, (totals.get(brand.accountId) ?? 0) + 1));

  return totals;
});

const accountGroups = computed(() => {
  if (!groupingActive.value) return [];

  const byAccount = new Map<string, any[]>();

  filteredBrands.value.forEach(brand => {
    byAccount.set(brand.accountId, [...(byAccount.get(brand.accountId) ?? []), brand]);
  });

  return [...byAccount.entries()]
    .map(([accountId, brands]) => ({
      accountId,
      brands,
      label: childNameById.value.get(accountId) ?? 'Other Brands',
      matchedCount: brands.length,
      totalCount: totalsByAccount.value.get(accountId) ?? brands.length,
    }))
    .sort((a, b) => b.totalCount - a.totalCount || a.label.localeCompare(b.label));
});

const collapsedByDefault = computed(
  () => totalsByAccount.value.size > GROUP_COLLAPSE_THRESHOLD || currentBrands.value.length > BRAND_COLLAPSE_THRESHOLD,
);

// Search auto-expands every matched group; non-matching groups are removed, not rendered empty.
function isGroupCollapsed(accountId: string) {
  if (query.value) return false;

  return !(expandedOverrides.value[accountId] ?? !collapsedByDefault.value);
}

function toggleGroup(accountId: string) {
  expandedOverrides.value = { ...expandedOverrides.value, [accountId]: isGroupCollapsed(accountId) };
}

const allGroupsExpanded = computed(
  () => accountGroups.value.length > 0 && accountGroups.value.every(group => !isGroupCollapsed(group.accountId)),
);

function toggleAllGroups() {
  const expand = !allGroupsExpanded.value;
  expandedOverrides.value = Object.fromEntries(accountGroups.value.map(group => [group.accountId, expand]));
}

const pluralize = (count: number, noun: string) => (count === 1 ? `${count} ${noun}` : `${count} ${noun}s`);

const searchSummary = computed(() => {
  if (!groupingActive.value || !query.value || !filteredBrands.value.length) return '';

  return `${pluralize(filteredBrands.value.length, 'Brand')} in ${pluralize(accountGroups.value.length, 'Child Account')}`;
});

const hiddenGroupCount = computed(() => {
  if (!groupingActive.value || !query.value || !filteredBrands.value.length) return 0;

  return Math.max(totalsByAccount.value.size - accountGroups.value.length, 0);
});

/** The 1v edge state fires only when a no-result query exactly matches a Child Account name. */
const exactChildMatch = computed(() => {
  if (!groupingActive.value || !query.value.trim() || filteredBrands.value.length) return null;

  const normalized = query.value.trim().toLowerCase();

  return childAccounts.value.find(child => child.name.toLowerCase() === normalized) ?? null;
});

const exactChildMatchBrandCount = computed(() =>
  exactChildMatch.value ? (totalsByAccount.value.get(exactChildMatch.value.id) ?? 0) : 0,
);

function openChildAccount(child: ChildAccountSummary) {
  query.value = '';
  expandedOverrides.value = { [child.id]: true };
}

const childInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');

// Highlights the matched substring (design 1u); fuzzy-only matches simply render unhighlighted.
function highlightSegments(name: string) {
  const normalizedQuery = query.value.trim().toLowerCase();
  const index = normalizedQuery ? name.toLowerCase().indexOf(normalizedQuery) : -1;

  if (index === -1) return [{ matched: false, text: name }];

  return [
    { matched: false, text: name.slice(0, index) },
    { matched: true, text: name.slice(index, index + normalizedQuery.length) },
    { matched: false, text: name.slice(index + normalizedQuery.length) },
  ].filter(segment => segment.text.length > 0);
}

function handleChange(value: string) {
  currentTab.value = value;
}

function setAllStores() {
  const selectedStores = (
    stores.value[currentTab.value as keyof typeof stores.value] as unknown as StoreWithSettings[]
  ).map(
    store =>
      ({
        accountId: store.accountId,
        capabilities: store.capabilities,
        generalSettings: store.generalSettings,
        id: store.id,
        logo: store.settings?.storeLogoUrl,
        name: store.name,
        platformId: store.platformId,
        settings: {
          storeLogoUrl: store.settings?.storeLogoUrl,
        },
        slug: store.slug,
        storeDomain: store.storeDomain,
        storeType: store.storeType,
        storeUrl: store.storeUrl,
      }) as Brand,
  );

  store.setCurrentStore(selectedStores);
  emit('close');
}

function closeStores(brand) {
  store.setCurrentStore(brand);
  emit('close');
}

watch(
  () => props.open,
  async open => {
    if (open) {
      query.value = '';
      expandedOverrides.value = {};

      await nextTick();
      searchInput.value?.$el.querySelector('input')?.focus();
    }
  },
);

onMounted(() => {
  window.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      emit('close');
    }
  });
});
</script>
